import type {
  LocationTunnelWashPackages,
  PackageTunnelPackageLink,
  TunnelWashPackageOption,
} from '../types/package-tunnel-wash.types';

/**
 * Package form "Tunnel wash package" field (CWS-1921): which tunnel wash package a car
 * wash package starts at each of its locations. Pure display / payload rules.
 */

export const TUNNEL_WASH_FIELD_LABEL = 'Tunnel wash package';
/** The null choice: the tunnel wash package is picked by hand at the tunnel. */
export const TUNNEL_WASH_MANUAL_LABEL = 'Manual';
export const TUNNEL_WASH_FIELD_HINT =
  'Manual: the tunnel wash is started by hand for this package.';

/** Car wash wash types that go through the tunnel (not detailing, add-ons or prepaid books). */
const TUNNEL_WASH_TYPES = ['single_wash', 'monthly_membership'];

export interface TunnelWashFieldOption {
  value: string | null;
  label: string;
}

export interface TunnelWashField {
  locationId: string;
  label: string;
  options: TunnelWashFieldOption[];
}

export interface TunnelWashFieldLocation {
  id: string;
  branch?: string | null;
  name?: string | null;
}

function asId(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function mapOption(raw: Record<string, unknown>): TunnelWashPackageOption {
  const relay = Number(raw['relay']);
  return {
    id: asId(raw['id']),
    name: String(raw['name'] ?? ''),
    relay: Number.isFinite(relay) ? relay : 0,
  };
}

/** Normalises `GET package/tunnel-wash-packages` data; tolerant of a missing body. */
export function mapLocationTunnelWashPackages(data: unknown): LocationTunnelWashPackages[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map(row => {
      const tunnel = row['tunnel'] as Record<string, unknown> | null | undefined;
      const packages = Array.isArray(row['tunnel_packages'])
        ? (row['tunnel_packages'] as Record<string, unknown>[]).map(mapOption)
        : [];
      return {
        location_id: asId(row['location_id']),
        tunnel: tunnel ? { id: asId(tunnel['id']), name: String(tunnel['name'] ?? '') } : null,
        tunnel_packages: packages.filter(p => p.id),
      };
    })
    .filter(row => row.location_id);
}

/** Only car wash packages (single wash / membership) can start a tunnel wash. */
export function isTunnelWashEligiblePackage(
  packageType: string | null | undefined,
  washTypes: readonly string[] | null | undefined
): boolean {
  return (
    packageType === 'car_wash' &&
    Array.isArray(washTypes) &&
    washTypes.some(type => TUNNEL_WASH_TYPES.includes(type))
  );
}

/** Ids of the selected locations, for the options request (sorted: stable cache key). */
export function tunnelWashLocationIds(locations: readonly TunnelWashFieldLocation[]): string[] {
  return [...new Set(locations.map(l => asId(l?.id)).filter(Boolean))].sort();
}

export function tunnelWashOptionLabel(option: TunnelWashPackageOption): string {
  return `${option.name} (c-relay ${option.relay})`;
}

/**
 * One dropdown per selected location whose tunnel has wash packages, in the order the
 * locations are selected. Locations without a tunnel or packages get no field, so shops
 * without tunnels see the form exactly as before.
 */
export function buildTunnelWashFields(
  selected: readonly TunnelWashFieldLocation[],
  available: readonly LocationTunnelWashPackages[]
): TunnelWashField[] {
  const byLocation = new Map(available.map(row => [row.location_id, row] as const));
  const named = selected.length > 1;
  const fields: TunnelWashField[] = [];
  for (const location of selected) {
    const row = byLocation.get(asId(location.id));
    if (!row?.tunnel || row.tunnel_packages.length === 0) {
      continue;
    }
    const locationName = (location.branch || location.name || '').trim();
    fields.push({
      locationId: row.location_id,
      label:
        named && locationName
          ? `${TUNNEL_WASH_FIELD_LABEL} — ${locationName}`
          : TUNNEL_WASH_FIELD_LABEL,
      options: [
        { value: null, label: TUNNEL_WASH_MANUAL_LABEL },
        ...row.tunnel_packages.map(option => ({
          value: option.id,
          label: tunnelWashOptionLabel(option),
        })),
      ],
    });
  }
  return fields;
}

/** Saved choices of an existing package: `locations[].tunnel_package_id`. */
export function tunnelSelectionsFromPackage(locations: unknown): Record<string, string | null> {
  const selections: Record<string, string | null> = {};
  if (!Array.isArray(locations)) {
    return selections;
  }
  for (const location of locations) {
    if (!location || typeof location !== 'object') {
      continue;
    }
    const row = location as Record<string, unknown>;
    const id = asId(row['id'] ?? row['location_id']);
    if (id) {
      const tunnelPackageId = asId(row['tunnel_package_id']);
      selections[id] = tunnelPackageId || null;
    }
  }
  return selections;
}

/** The field's current value: the saved/picked id when it is still offered, else Manual. */
export function resolveTunnelWashSelection(
  field: TunnelWashField,
  selections: Readonly<Record<string, string | null>>
): string | null {
  const picked = selections[field.locationId] ?? null;
  return field.options.some(option => option.value === picked) ? picked : null;
}

/** `tunnel_packages` of the save body: one entry per field shown. */
export function buildTunnelPackagesPayload(
  fields: readonly TunnelWashField[],
  selections: Readonly<Record<string, string | null>>
): PackageTunnelPackageLink[] {
  return fields.map(field => ({
    location_id: field.locationId,
    tunnel_package_id: resolveTunnelWashSelection(field, selections),
  }));
}
